import React, { Component, Fragment } from 'react';
import isEqual from 'lodash/isEqual';
import params from '../../utils/queryParams';

import {
  numberWithCommas,
  debounce,
  getNestedValue,
  checkNestedKeys
} from '../../utils/utils';

import Card from '../Card/Card';
import CSVExport from '../CSVExport/CSVExport';
import SearchInputDebounce from '../SearchInputDebounce/SearchInputDebounce';
import HeaderInfoTooltip from '../HeaderInfoTooltip/HeaderInfoTooltip';
import TablePagination from '../TablePagination/TablePagination';
import Overlay from '../Overlay/Overlay';

import { Icon } from 'semantic-ui-react';

import FilterBox from './FilterBox';

const PAGE_SIZE = 50;
const DEFAULT_CONTENT_MIN_WIDTH = 1600;

// Direction def: DESC = High to Low = down arrow (because I always get this mixed up)
const sortTypeLabels = {
  alpha: { asc: 'A to Z', desc: 'Z to A' },
  numeric: { asc: 'Low to High', desc: 'High to Low' },
  date: { asc: 'Oldest First', desc: 'Newest First' },
  timeline: { asc: 'Oldest First', desc: 'Farthest First' }
};

let abortController = null;

export class DataTable extends Component {
  constructor(props) {
    super(props);

    this.tableRef = React.createRef();
    this.tableWrapperRef = React.createRef();
    this.tableTopScrollRef = React.createRef();
    const si = props.searchInput || {};
    // if there is an incoming search token, and the table name matches, set it
    const searchQuery =
      si.searchTerm && si.tableName === props.tableName ? si.searchTerm : '';

    let qpSearch = params.get('s');
    this.state = {
      needToSort: true,
      originalData: [],
      searchQuery: qpSearch ? decodeURIComponent(qpSearch) : searchQuery,
      searchedData: [],
      sortedData: [],
      pageNumber: 1,
      sortColumn: props.queryParams['sort_col'] || props.defaultSort,
      sortDirection:
        props.queryParams['sort_dir'] || props.defaultSortDirection,
      shouldSendAnalytics: false,
      isFiltered: false,
      canTableScroll: !!props.preventScrollId,
      totalCount: null,
      selectedSortColumnPopup: null
    };
  }

  componentDidMount() {
    this.setWidths();
    document.addEventListener('scroll', this.handleScroll, true);

    const { searchInput, tableName } = this.props;
    const sitName = (searchInput && searchInput.tableName) || '';
    // reset the cached search token if this is not either THE table, or its child details table
    if (sitName !== tableName && `${sitName}_details` !== tableName) {
      // reset values if the tables are different
      this.props.searchInputFunction('', tableName);
    }
    this.filterData();
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.handleScroll, true);
  }

  componentDidUpdate(prevProps, prevState) {
    const { data, columns } = this.props;
    const prevData = prevProps.data;
    if (!isEqual(data, prevData)) {
      if (!data || !data.length > 0) {
        this.setState({
          originalData: [],
          searchedData: [],
          sortedData: [],
          displayData: null
        });
      } else {
        this.setState(
          {
            pageNumber: 1,
            originalData: data,
            needToSort: true
          },
          () => {
            this.filterData();
          }
        );
      }
    }
    if (!isEqual(columns, prevProps.columns)) {
      this.setWidths();
    }
  }

  handleScroll = (e) => {
    const { canTableScroll } = this.state;
    const { preventScrollId } = this.props;

    if (e.target.id === preventScrollId) {
      const el = e.target;
      const scrollableDistance = el.scrollHeight - el.offsetHeight;
      const position = el.scrollTop;

      if (position >= scrollableDistance && !canTableScroll) {
        this.setState({ canTableScroll: true });
      } else if (position < scrollableDistance && canTableScroll) {
        this.setState({ canTableScroll: false });
      }
    }
  };

  setWidths = () => {
    const { columns, minTablePixelWidth } = this.props;

    // we get the width of the .Table element. subtract the widths of all the set width columns.
    // then we distribute the rest.
    const tableWidth = !!minTablePixelWidth
      ? minTablePixelWidth
      : DEFAULT_CONTENT_MIN_WIDTH;
    let noSetWidthCount = 0;
    const fixedColumnsSum = columns.reduce((accumulator, col) => {
      // const filterBufferWidth = 100 // !!col.sortType ? 100 : 0; // width added to allow for space for sort-icon
      const strWidth = col.width || 0;
      if (!strWidth) {
        noSetWidthCount++;
        return accumulator;
      }

      // if its a %, calculate by multiplying it by the total width;
      let calculatedWidth = 0;
      if (strWidth && typeof strWidth === 'string') {
        // percent path
        calculatedWidth = parseInt(strWidth.replace(/\D/g, '')); //+ filterBufferWidth;
        if (strWidth.includes('%')) {
          // strip other chars and return number
          calculatedWidth =
            calculatedWidth > 0
              ? Math.floor((calculatedWidth / 100) * tableWidth)
              : 0; // + filterBufferWidth;
        }
      }

      return accumulator + calculatedWidth;
    }, 0);
    const leftOver = tableWidth - fixedColumnsSum;

    const portion = `${Math.round(
      noSetWidthCount > 0 ? leftOver / noSetWidthCount : 50
    )}px`;

    const newColumns = columns.map((col) => {
      return { ...col, width: col.width || portion };
    });

    this.setState({ columns: newColumns });
  };

  validateImportantProps = () => {
    const {
      ErrorHandler,
      searchColumns,
      columns,
      data,
      defaultSort
    } = this.props;
    if (!data || !data.length) {
      return;
    }

    const sampleRow = data && data.length && data.length > 0 && data[0];

    // Validate column dataKeys
    columns.forEach((col) => {
      if (!sampleRow || !checkNestedKeys(col.dataKey, sampleRow)) {
        ErrorHandler.bug(
          `ERROR::DataTable::dataKey ${col.dataKey} is not valid!`
        );
      }
    });

    // Validate SearchColumns
    if (searchColumns && searchColumns.length > 0) {
      searchColumns.forEach((searchCol) => {
        if (!checkNestedKeys(searchCol, sampleRow)) {
          ErrorHandler.bug(
            `ERROR::DataTable::SearchColumn ${searchCol} is not valid!`
          );
        }
      });
    }

    // Validate defaultSort column only if a defaultSort has been provided
    if (defaultSort && !columns.find((col) => col.dataKey === defaultSort)) {
      ErrorHandler.bug(
        `ERROR::DataTable::defaultSort ${defaultSort} is not valid!`
      );
    }
  };

  //   --initial load--
  //   data ==>> searchFilter ==>> sort ==>> paginate ==>> display
  //   --on search--
  //   data ==>> searchFilter ==>> sort ==>> paginate ==>> display
  //   --on sort--
  //   searchData ==>> sort ==>> paginate ==>> display
  //   --on page--
  //   sortedData ==>> paginate ==>> display

  filterData = () => {
    if (abortController) {
      // if this was called recently and is still running, kill it.
      abortController.abort();
    }

    abortController = new AbortController();

    this.abortableFilterData(abortController.signal).catch((err) => null);
  };

  /*
   * This abortableFilterData allows us to abort the filter, and restart without having to wait for it to complete
   */
  abortableFilterData = (abortSignal) =>
    new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.filterDataAsync(resolve);
      }, 0);

      abortSignal.addEventListener('abort', () => {
        clearTimeout(timeout);
        return reject();
      });
    });

  filterDataAsync = (resolve) => {
    this.validateImportantProps();

    const { searchColumns, columns } = this.props;
    const { searchQuery, originalData } = this.state;

    // if there is no search term OR if there are no search columns return all originalData
    if (!searchColumns || !searchColumns.length > 0 || !searchQuery) {
      if (!this.state.needToSort) {
        this.setState({
          searchedData: originalData,
          sortedData: originalData
        });
      } else {
        this.setState({ searchedData: originalData }, () => {
          this.handleSort();
        });
      }
      return;
    }

    const searchedData = originalData.filter((row) => {
      let keep = false;

      for (const col of searchColumns) {
        const colConfig = columns.find(
          (c) => c.key === col || c.dataKey === col
        );
        // Here instead of just getting the raw value from the data, we should format it similar to how it appears on
        // the view so that people can search specifically what they are seeing... specially if we are formatting some
        // array or nested values.
        let val =
          colConfig &&
          colConfig.formatter &&
          colConfig.formatter(getNestedValue(col, row), row);

        // if we didn't get a string from formatting it, then fallback to the data value
        if (!val || typeof val !== 'string') {
          val = getNestedValue(col, row);
          // if the value is an array, join it into a string so we can search on it
          val = Array.isArray(val) ? val.join('') : val;
        }

        if (
          val &&
          typeof val === 'string' &&
          val.toLowerCase().includes(searchQuery.toLowerCase())
        ) {
          keep = true;
        }
      }

      return keep;
    });

    if (!this.state.needToSort) {
      this.setState({ searchedData, sortedData: searchedData });
    } else {
      this.setState({ searchedData }, () => {
        this.handleSort();
      });
    }

    resolve();
  };

  prepFieldForSort = (sortType, val, column) => {
    const { ErrorHandler } = this.props;
    let aVal = val;
    switch (sortType) {
      case 'number':
        if (typeof aVal === 'string') {
          aVal = parseFloat(aVal.replace(/[^\d.-]/g, ''));
        }
        if (!aVal) {
          aVal = 0;
        }
        break;
      case 'boolean':
        // should just work
        break;
      case 'string':
        aVal = typeof aVal === 'string' ? aVal.toLowerCase() : aVal;
        break;
      case 'date':
      case 'time':
        aVal = new Date(aVal).getTime();
        break;
      case 'array':
        // if we try to sort a column of arrays, just join the arrays into a big string, and string compare...
        // lets hope they have the values in the order they want. may consider sorting the strings first??
        aVal = Array.isArray(aVal) ? aVal.join('') : '';
        break;
      case 'object':
        // I don't know why anyone would try to sort by objects,
        // but if they do, we will just take the values in order
        // mash them into a string, then compare the strings. lol
        aVal = aVal ? Object.values(aVal).join('') : '';
        ErrorHandler.bug(
          'Attempting to sort a column of objects... are you sure you wired the data up right?'
        );
        break;
      default:
        ErrorHandler.bug(
          `ERROR::DataTable::Invalid type [${sortType}] provided for datatable column [${column}], sample value: ${JSON.stringify(
            aVal
          )}`
        );
    }
    return aVal;
  };

  handleHideSortPopup = () => {
    this.setState({ selectedSortColumnPopup: null });
  };

  handleSort = (dataKey, selectedDirection) => {
    let column = dataKey;
    const {
      createEvent,
      eventTypes,
      tableName,
      columns,
      secondarySort
    } = this.props;
    const {
      sortColumn,
      sortDirection,
      searchedData,
      shouldSendAnalytics
    } = this.state;

    let direction = sortDirection;

    if (dataKey) {
      // if we are passing a key, then its cause we clicked a header,
      // so we need to handle direction change if needed
      if (dataKey === sortColumn) {
        // if the column passed is the same as the currently sorted column, then switch direction
        direction = direction === 'ASC' ? 'DESC' : 'ASC';
      }

      // if a specific selected direction was passed, use that, else use the fallback toggle method
      direction = selectedDirection || direction;

      this.setState({ sortColumn: dataKey, sortDirection: direction });
      this.props.setQueryParams({
        ['sort_col']: encodeURIComponent(dataKey),
        ['sort_dir']: encodeURIComponent(direction)
      });
    } else {
      column = sortColumn;
    }

    if (shouldSendAnalytics) {
      // Analytics
      createEvent &&
        typeof createEvent === 'function' &&
        createEvent({
          action: eventTypes && eventTypes.TABLE_SORT,
          detail: `${tableName}/${dataKey}:${direction}`
        });
    }

    // find the item from the columns so that we can find its 'type'
    // also maybe we should allow the user to pass a sort function
    // on second thought, no, lets just keep expanding the sort types. we can have nice custom ones.

    // if dataKey is undefined, then we are doing the default sort, so use that as the key
    const sortItem = columns.find((col) => col.dataKey === column);
    let sortType = sortItem && sortItem.type;

    let sortedData = [];

    //gotchas  null values
    // mixed types?
    sortedData = searchedData.sort((a, b) => {
      let aVal = getNestedValue(column, a) || '';
      let bVal = getNestedValue(column, b) || '';
      let cVal = '';
      let dVal = '';

      if (secondarySort) {
        cVal = getNestedValue(secondarySort, a) || '';
        dVal = getNestedValue(secondarySort, b) || '';
      }

      // if a column sort type was not provided, try to get it manually
      sortType = sortType || typeof aVal;
      if (sortType === 'object') {
        sortType = Array.isArray(aVal) ? 'array' : 'object';
      }

      aVal = this.prepFieldForSort(sortType, aVal, column);
      bVal = this.prepFieldForSort(sortType, bVal, column);
      if (secondarySort) {
        let secondarySortType = typeof cVal;
        if (secondarySortType === 'object') {
          secondarySortType = Array.isArray(aVal) ? 'array' : 'object';
        }
        cVal = this.prepFieldForSort(secondarySortType, cVal, column);
        dVal = this.prepFieldForSort(secondarySortType, dVal, column);
      }

      const compareVals = (a, b, shouldSecondarySort) => {
        let result = 0;

        if (a < b) {
          result = 1;
        } else if (a > b) {
          result = -1;
        } else if (shouldSecondarySort) {
          compareVals(cVal, dVal, false);
        }

        return result;
      };

      return direction === 'DESC'
        ? compareVals(aVal, bVal, secondarySort)
        : compareVals(bVal, aVal, secondarySort);
    });

    this.setState({
      sortedData,
      needToSort: false,
      shouldSendAnalytics: false
    });
  };

  handleColumnHeaderClick = (dataKey) => {
    this.setState({ shouldSendAnalytics: true }, () => {
      //this.handleSort(dataKey);
      this.showColumnSortOptions(dataKey);
    });
  };

  showColumnSortOptions = (dataKey) => {
    // if this column is already shown, then we must be trying to toggle it off
    this.setState({
      selectedSortColumnPopup:
        this.state.selectedSortColumnPopup === dataKey ? null : dataKey
    });
  };

  handlePageChange = (page) => {
    this.tableTopScrollRef.current.scrollIntoView({
      block: 'start'
    });
    this.setState({ pageNumber: page });
  };

  handleQueryChange = (value) => {
    // so setting state here is causing it to render first, then calling the filter debounce next is a whole nother render

    const { tableName, createEvent, eventTypes } = this.props;
    if (typeof this.props.setQueryParams === 'function') {
      this.props.setQueryParams({ s: encodeURIComponent(value) });
    }
    createEvent &&
      typeof createEvent === 'function' &&
      createEvent({
        action: eventTypes && eventTypes.TEXT_FILTER,
        detail: `${tableName}:${value}`
      });

    this.setState({ searchQuery: value, pageNumber: 1 }, this.filterData);
  };

  handleRowClick = (row) => {
    if (this.props.onRowClick && typeof this.props.onRowClick === 'function') {
      this.props.searchInputFunction(
        this.state.searchQuery,
        this.props.tableName
      );
      this.props.onRowClick(row);
    }
  };

  handleFilterChange = ({ filteredData, isFiltered, totalCount }) => {
    this.setState({ originalData: filteredData, isFiltered, totalCount }, () =>
      this.filterData()
    );
  };

  render() {
    const {
      sortedData,
      originalData,
      pageNumber,
      sortColumn,
      sortDirection,
      searchQuery,
      widths,
      isFiltered,
      totalCount,
      canTableScroll,
      selectedSortColumnPopup,
      columns
    } = this.state;

    const {
      pageName,
      csvColumns,
      csvFilterCategories,
      columnTotals,
      className,
      isLoading,
      emptyStateMessage,
      noResultsMessage,
      title,
      subtitle,
      additionalFilters,
      searchColumns,
      tableProps,
      rowProps,
      searchPlaceholder,
      dataType,
      isEmpty,
      minTablePixelWidth,
      data,
      csvCustomFileName,
      filterCategories
    } = this.props;

    let displayData = [];
    let itemCount = 0;

    if (sortedData && sortedData.length > 0) {
      const startIndex = PAGE_SIZE * (pageNumber - 1);
      const endIndex = PAGE_SIZE * pageNumber;
      displayData =
        (sortedData &&
          sortedData.length > 0 &&
          sortedData.slice(startIndex, endIndex)) ||
        [];
      itemCount = sortedData.length;
    }
    const filterCat = !!filterCategories ? filterCategories : [];
    const isNoData =
      isEmpty || !data || !Array.isArray(data) || data.length === 0;

    // if a callback function was passed as a prop for the onRowClick,
    // then the rows are clickable, and apply any applicable styles/functionality
    const isRowClickable = typeof this.props.onRowClick === 'function';

    const unmodifiedTitle = !title && !!subtitle ? subtitle : title;

    const modifiedTitle = (
      <Fragment>
        {!!unmodifiedTitle && unmodifiedTitle}
        {isFiltered && typeof this.props.filterCountFormatter === 'function' ? (
          this.props.filterCountFormatter({ itemCount, totalCount })
        ) : (
          <p
            style={{
              display: !!subtitle ? 'inline-block' : 'block',
              borderLeft:
                !!dataType && !!subtitle ? '1px solid #a1a3a4' : 'none',
              fontSize: '12px',
              height: '19px',
              fontWeight: 'normal',
              color: '#a1a3a4',
              margin: !!subtitle ? '0 0 0 10px' : '0',
              paddingLeft: !!subtitle ? '10px' : '0'
            }}>
            {!!dataType && `Showing ${itemCount} of ${totalCount} ${dataType}`}
          </p>
        )}
      </Fragment>
    );

    return (
      <div className="DataTableWrapper" ref={this.tableWrapperRef}>
        <span
          style={
            { position: 'relative', top: '-100px' }
            /* yeah, this looks sketchy. basically need to scroll up 100px above the top of the table because of
             * overlapping hover titlebars.
             */
          }
          ref={this.tableTopScrollRef}></span>
        <Card
          className={`${className || ''}${modifiedTitle ? ' has-title' : ''}`}
          isLoading={isLoading}
          title={isNoData ? null : modifiedTitle}
          subtitle={isNoData || !title ? null : subtitle}>
          <div className="DataTableV3" ref={this.tableRef}>
            {(!!additionalFilters || !isNoData) && (
              <div className="filterRow">
                {additionalFilters}
                {!!searchColumns && searchColumns.length > 0 && (
                  <SearchInputDebounce
                    onSearch={this.handleQueryChange}
                    initialValue={searchQuery}
                    placeholder={searchPlaceholder || 'Search'}
                  />
                )}
                {csvFilterCategories && (
                  <CSVExport
                    pageName={pageName}
                    tableColumns={csvColumns || columns}
                    filterCategories={csvFilterCategories}
                    data={originalData}
                    filename={csvCustomFileName}
                  />
                )}
              </div>
            )}
            {!isNoData && (
              <FilterBox
                setQueryParams={this.props.setQueryParams}
                queryParams={this.props.queryParams}
                ErrorHandler={this.props.ErrorHandler}
                page="/storybook"
                filterCategories={filterCat}
                data={data}
                onChange={this.handleFilterChange}
              />
            )}
            {displayData.length ? (
              <div className="DataTableContent">
                <Table
                  disableVerticalScroll={!canTableScroll}
                  style={{
                    minWidth: minTablePixelWidth
                      ? `${minTablePixelWidth}px`
                      : `${DEFAULT_CONTENT_MIN_WIDTH}px`,
                    minHeight: '120px'
                  }}
                  {...tableProps}>
                  <TableHeaderRow>
                    {columns.map((d, index) => (
                      <TableHeaderCell
                        key={index}
                        sortType={d.sortType}
                        style={Object.assign(
                          {},
                          d.style,
                          d.headerStyle,
                          d.width ? { width: d.width } : {},
                          {
                            justifyContent:
                              d.textAlign === 'right'
                                ? 'flex-end'
                                : d.textAlign === 'center'
                                ? 'center'
                                : 'flex-start'
                          }
                        )}
                        sorted={
                          sortColumn === d.dataKey
                            ? sortDirection === 'DESC'
                              ? 'descending'
                              : 'ascending'
                            : null
                        }
                        onClick={() => {
                          this.handleColumnHeaderClick(d.dataKey);
                        }}>
                        <HeaderInfoTooltip content={d.description}>
                          <span className="title">
                            {d.name}{' '}
                            {columnTotals &&
                            columnTotals.hasOwnProperty(d.dataKey)
                              ? `(${numberWithCommas(columnTotals[d.dataKey])})`
                              : null}
                          </span>
                        </HeaderInfoTooltip>
                        {!!d.sortType && (
                          <SortOptionsPopup
                            dataKey={d.dataKey}
                            isVisible={selectedSortColumnPopup === d.dataKey}
                            sortType={d.sortType}
                            onSort={this.handleSort}
                            sorted={sortColumn === d.dataKey}
                            direction={sortDirection}
                            hideSortOptionPopup={this.handleHideSortPopup}
                          />
                        )}
                      </TableHeaderCell>
                    ))}
                  </TableHeaderRow>
                  <TableBody>
                    {displayData.map((row, index) => (
                      <TableRow
                        {...rowProps}
                        key={index}
                        className={index === 0 ? 'first-table-row' : null}
                        style={
                          typeof this.props.rowStyleFormatter === 'function'
                            ? this.props.rowStyleFormatter(row, index)
                            : {}
                        }
                        onClick={() => {
                          this.handleRowClick(row);
                        }}>
                        {columns.map((col, colIndex) => (
                          <TableCell
                            {...(col.cellProps || {})}
                            key={colIndex}
                            style={Object.assign(
                              {
                                cursor: isRowClickable ? 'pointer' : 'default'
                              },
                              col.style,
                              col.cellStyle,
                              col.width ? { width: col.width } : {},
                              { textAlign: col.textAlign || 'left' }
                            )}>
                            {typeof col.formatter === 'function'
                              ? col.formatter(
                                  getNestedValue(col.dataKey, row),
                                  row
                                )
                              : getNestedValue(col.dataKey, row)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div
                className="well"
                style={{
                  marginLeft: '50%',
                  transform: 'translateX(-50%)'
                }}>
                {isNoData
                  ? emptyStateMessage || 'No data found.'
                  : noResultsMessage || 'No matches found'}
              </div>
            )}
            <TablePagination
              activePage={pageNumber}
              onPageChange={this.handlePageChange}
              itemCount={itemCount}
              perPage={PAGE_SIZE}
            />
          </div>
        </Card>
      </div>
    );
  }
}

export default DataTable;

const Table = ({
  children,
  className,
  ref,
  disableVerticalScroll,
  ...otherProps
}) => (
  <div
    className={[
      'Table',
      className,
      disableVerticalScroll && 'no-vertical-scroll'
    ]
      .filter((f) => !!f)
      .join(' ')}
    {...otherProps}>
    {children}
  </div>
);
const TableRow = ({ children, className, ...otherProps }) => (
  <div
    className={['TableRow', className].filter((f) => !!f).join(' ')}
    {...otherProps}>
    {children}
  </div>
);
const TableCell = ({ children, className, ...otherProps }) => (
  <div
    className={['TableCell', className].filter((f) => !!f).join(' ')}
    {...otherProps}>
    {children}
  </div>
);
const TableBody = ({ children, className }) => (
  <div className={['TableBody', className].filter((f) => !!f).join(' ')}>
    {children}
  </div>
);
const TableHeaderRow = ({ children, className }) => (
  <div className={['TableHeaderRow', className].filter((f) => !!f).join(' ')}>
    {children}
  </div>
);
const TableHeaderCell = ({
  children,
  className,
  sorted,
  sortType,
  ...otherProps
}) => (
  <div
    className={[
      'TableHeaderCell',
      sorted && `sorted ${sorted}`,
      sortType && 'sortable-column',
      className
    ]
      .filter((f) => !!f)
      .join(' ')}
    {...otherProps}>
    {children}
    {!!sortType && (
      <i className="icon-stack">
        <i className="sort-up" />
        <i className="sort-down" />
      </i>
    )}
  </div>
);

const SortOptionsPopup = ({
  dataKey,
  isVisible,
  sortType,
  onSort,
  sorted,
  hideSortOptionPopup,
  direction
}) => {
  // grab a list of all the actual sort type options
  const sortOpts = Object.keys(sortTypeLabels);
  // if the sort type passed does not exist, warn the developer of their errant ways
  if (sortType && !sortOpts.includes(sortType)) {
    console.warn(
      `Error: invalid sort type provided to datatable column of ${sortType}, instead try one of: ${sortOpts.join(
        ', '
      )}`
    );
    return null;
  }

  return isVisible ? (
    <>
      <Overlay
        isVisible={isVisible}
        zIndex={99}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          hideSortOptionPopup();
        }}
      />
      <div className="SortOptionsPopup" style={{ zIndex: '100' }}>
        <div className="option desc" onClick={() => onSort(dataKey, 'DESC')}>
          <span
            className={[
              'sort-option-checkmark',
              sorted && direction === 'DESC' && 'visible'
            ]
              .filter((f) => !!f)
              .join(' ')}>
            <Icon name="checkmark" />
          </span>
          {sortTypeLabels[sortType].desc}
        </div>
        <div className="option asc" onClick={() => onSort(dataKey, 'ASC')}>
          <span
            className={[
              'sort-option-checkmark',
              sorted && direction === 'ASC' && 'visible'
            ]
              .filter((f) => !!f)
              .join(' ')}>
            <Icon name="checkmark" />
          </span>
          {sortTypeLabels[sortType].asc}
        </div>
      </div>
    </>
  ) : null;
};
/*
    <Overlay
      isVisible={isVisible}
      zIndex={2000}
      opacity={0.5}
      color="black"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        hideSortOptionPopup();
      }}>
    </Overlay>

    */
